import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'

import { cn } from '@/lib/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-lg font-normal transition-colors focus:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-system-selected-blue  disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-[0.36]',
  {
    variants: {
      variant: {
        default:
          'bg-system-primary-blue text-white hover:bg-system-accent-hover',
        accent:
          'bg-system-accent-default text-white hover:bg-system-accent-hover',
        destructive: 'bg-system-state-error text-white',
        secondary:
          'border border-system-border-secondary bg-white text-system-primary-blue hover:border-system-border-primary hover:bg-system-background-grey',
        ghost:
          'text-system-primary-blue focus:border-2 focus:border-system-border-secondary',
        link: 'text-system-text-link underline-offset-4 hover:underline',
        social:
          'gap-2 border border-zinc-200 bg-white text-sm font-medium text-system-text-secondary shadow-sm hover:bg-zinc-200',
        slate:
          'bg-system-text-slate-blue text-white hover:bg-system-accent-hover',
      },
      size: {
        default: 'h-10 px-3 py-2 text-sm',
        sm: 'h-9 px-2 py-1 text-xs',
        lg: 'h-11 px-8',
        icon: 'size-10',
        link: 'h-auto text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants }
